import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import AuroraBackground from './AuroraBackground';
import Button from './ui/Button';
import StatCard from './ui/StatCard';
import './Tokenizer.css';

// Claude (Anthropic) and Grok (xAI) are intentionally absent: neither ships a
// public local tokenizer for current models, so accurate counts would require
// their count-tokens APIs (and an API key). See docs/ADDING_MODELS.md.
const MODELS = [
  { id: 'gpt-5', name: 'GPT-5', encoding: 'o200k_base', provider: 'OpenAI', description: 'o200k_base' },
  { id: 'gpt-5-mini', name: 'GPT-5 mini', encoding: 'o200k_base', provider: 'OpenAI', description: 'o200k_base' },
  { id: 'gpt-5-nano', name: 'GPT-5 nano', encoding: 'o200k_base', provider: 'OpenAI', description: 'o200k_base' },
  { id: 'gpt-4.1', name: 'GPT-4.1', encoding: 'o200k_base', provider: 'OpenAI', description: 'o200k_base' },
  { id: 'gpt-4.1-mini', name: 'GPT-4.1 mini', encoding: 'o200k_base', provider: 'OpenAI', description: 'o200k_base' },
  { id: 'gpt-4o', name: 'GPT-4o', encoding: 'o200k_base', provider: 'OpenAI', description: 'o200k_base' },
  { id: 'gpt-4o-mini', name: 'GPT-4o mini', encoding: 'o200k_base', provider: 'OpenAI', description: 'o200k_base' },
  { id: 'o3', name: 'o3', encoding: 'o200k_base', provider: 'OpenAI', description: 'o200k_base' },
  { id: 'o4-mini', name: 'o4-mini', encoding: 'o200k_base', provider: 'OpenAI', description: 'o200k_base' },
  { id: 'gpt-4', name: 'GPT-4', encoding: 'cl100k_base', provider: 'OpenAI (legacy)', description: 'cl100k_base' },
  { id: 'gpt-3.5-turbo', name: 'GPT-3.5 Turbo', encoding: 'cl100k_base', provider: 'OpenAI (legacy)', description: 'cl100k_base' },
  { id: 'custom-word', name: 'Words', encoding: null, provider: 'Custom', description: 'simple word split' },
  { id: 'custom-char', name: 'Characters', encoding: null, provider: 'Custom', description: 'character split' },
];

const PROVIDERS = [...new Set(MODELS.map((m) => m.provider))];

const SAMPLE_TEXT =
  'This is a sample text to demonstrate tokenization. It contains multiple sentences and different types of words!';

// Encoders are heavy (the rank tables are ~1-2 MB), so they are loaded
// on demand via dynamic import and cached per encoding name.
const encoderCache = {};

function getEncoder(encoding) {
  if (!encoderCache[encoding]) {
    encoderCache[encoding] = (async () => {
      const { Tiktoken } = await import('js-tiktoken/lite');
      const ranks =
        encoding === 'o200k_base'
          ? await import('js-tiktoken/ranks/o200k_base')
          : await import('js-tiktoken/ranks/cl100k_base');
      return new Tiktoken(ranks.default ?? ranks);
    })();
  }
  return encoderCache[encoding];
}

async function tokenize(text, modelId) {
  if (!text) return [];

  if (modelId === 'custom-word') {
    return text
      .split(/\s+/)
      .filter(Boolean)
      .map((word, i) => ({ text: word, id: i + 1 }));
  }

  if (modelId === 'custom-char') {
    return [...text].map((char) => ({ text: char, id: char.codePointAt(0) }));
  }

  const model = MODELS.find((m) => m.id === modelId);
  const encoder = await getEncoder(model.encoding);
  // Treat special-token strings like <|endoftext|> as plain text.
  const ids = encoder.encode(text, [], []);
  return ids.map((id) => ({ text: encoder.decode([id]), id }));
}

export default function Tokenizer() {
  const [searchParams] = useSearchParams();
  const [inputText, setInputText] = useState(() => searchParams.get('text') || '');
  const [selectedModel, setSelectedModel] = useState('gpt-5');
  const [tokens, setTokens] = useState([]);
  const [showIds, setShowIds] = useState(false);
  const [copied, setCopied] = useState(false);
  const requestRef = useRef(0);

  const runTokenize = useCallback((text, modelId) => {
    const requestId = ++requestRef.current;
    tokenize(text, modelId)
      .then((result) => {
        if (requestRef.current === requestId) setTokens(result);
      })
      .catch((error) => {
        console.error('Tokenization error:', error);
        if (requestRef.current === requestId) setTokens([]);
      });
  }, []);

  useEffect(() => {
    runTokenize(inputText, selectedModel);
  }, [inputText, selectedModel, runTokenize]);

  const wordCount = inputText.split(/\s+/).filter(Boolean).length;

  const copyTokenIds = () => {
    navigator.clipboard
      .writeText(JSON.stringify(tokens.map((t) => t.id)))
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1800);
      })
      .catch((err) => console.error('Failed to copy:', err));
  };

  return (
    <div className="tokenizer">
      <AuroraBackground />

      <header className="tokenizer__nav">
        <Link to="/" className="tokenizer__logo">
          AB<span className="gradient-text">·17</span>
        </Link>
        <Link to="/" className="tokenizer__back">
          ← Back to portfolio
        </Link>
      </header>

      <main className="tokenizer__main">
        <div className="tokenizer__header">
          <span className="tokenizer__eyebrow">~/tools $ tokenize</span>
          <h1 className="tokenizer__title">
            Tokenizer <span className="gradient-text">Tool</span>
          </h1>
          <p className="tokenizer__subtitle">
            Real token counts and IDs for OpenAI models, computed in your browser with js-tiktoken.
          </p>
        </div>

        <section className="tokenizer__panel">
          <div className="tokenizer__controls">
            <label className="tokenizer__label" htmlFor="model-select">
              Model
            </label>
            <select
              id="model-select"
              value={selectedModel}
              onChange={(e) => setSelectedModel(e.target.value)}
              className="tokenizer__select"
            >
              {PROVIDERS.map((provider) => (
                <optgroup key={provider} label={provider}>
                  {MODELS.filter((m) => m.provider === provider).map((model) => (
                    <option key={model.id} value={model.id}>
                      {model.name} ({model.description})
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>

          <label className="tokenizer__label" htmlFor="text-input">
            Text
          </label>
          <textarea
            id="text-input"
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            placeholder="Paste or type your text here…"
            rows={7}
            className="tokenizer__textarea"
          />

          <div className="tokenizer__actions">
            <Button variant="subtle" onClick={() => setInputText(SAMPLE_TEXT)}>
              Load Sample
            </Button>
            <Button variant="ghost" onClick={() => setInputText('')}>
              Clear
            </Button>
          </div>
        </section>

        <section className="tokenizer__stats">
          <StatCard value={String(tokens.length)} label="Tokens" />
          <StatCard value={String(wordCount)} label="Words" />
          <StatCard value={String(inputText.length)} label="Characters" />
        </section>

        {tokens.length > 0 ? (
          <section className="tokenizer__panel">
            <div className="tokenizer__tokens-header">
              <h2 className="tokenizer__tokens-title">Token Breakdown</h2>
              <div className="tokenizer__tokens-actions">
                <button
                  type="button"
                  className={`tokenizer__toggle ${!showIds ? 'is-active' : ''}`}
                  onClick={() => setShowIds(false)}
                >
                  Text
                </button>
                <button
                  type="button"
                  className={`tokenizer__toggle ${showIds ? 'is-active' : ''}`}
                  onClick={() => setShowIds(true)}
                >
                  Token IDs
                </button>
                <Button variant="subtle" onClick={copyTokenIds}>
                  {copied ? 'Copied ✓' : 'Copy IDs'}
                </Button>
              </div>
            </div>
            <div className="tokenizer__tokens" translate="no">
              {tokens.map((token, index) => (
                <span
                  key={`${index}-${token.id}`}
                  className={`tokenizer__token tokenizer__token--c${index % 5}`}
                  title={showIds ? JSON.stringify(token.text) : `ID: ${token.id}`}
                >
                  {showIds ? token.id : token.text}
                </span>
              ))}
            </div>
          </section>
        ) : (
          <p className="tokenizer__empty">Enter some text above to see the token breakdown.</p>
        )}
      </main>
    </div>
  );
}
